/**
 * Shared chart utilities used by WidgetRenderer.
 */
import {
  getAnalytics,
  listMeasurements,
  type MeasurementRecord,
} from "@/lib/api/building";

export type TrendChannel =
  | "co2_ppm"
  | "indoor_temperature"
  | "electricity_kwh"
  | "heat_kwh"
  | "water_m3"
  | "occupancy"
  | "ventilation_rate"
  | "energy_kwh";

export interface ChannelMeta {
  label: string;
  unit: string;
  color: string;
}

export const TREND_CHANNEL_META: Record<TrendChannel, ChannelMeta> = {
  co2_ppm: { label: "CO₂", unit: "ppm", color: "#a78bfa" },
  indoor_temperature: { label: "Lämpötila", unit: "°C", color: "#f97316" },
  electricity_kwh: { label: "Sähkö", unit: "kWh", color: "#3b82f6" },
  heat_kwh: { label: "Lämpö", unit: "kWh", color: "#f43f5e" },
  water_m3: { label: "Vesi", unit: "m³", color: "#38bdf8" },
  occupancy: { label: "Käyttöaste", unit: "%", color: "#4ade80" },
  ventilation_rate: { label: "Ilmanvaihto", unit: "%", color: "#4db6ac" },
  energy_kwh: { label: "Energia (agg.)", unit: "kWh", color: "#fbbf24" },
};

export const MEASUREMENT_CHANNELS = new Set<TrendChannel>([
  "co2_ppm",
  "indoor_temperature",
  "electricity_kwh",
  "heat_kwh",
  "water_m3",
]);

export const ANALYTICS_CHANNELS = new Set<TrendChannel>([
  "occupancy",
  "ventilation_rate",
  "energy_kwh",
]);

export interface ChartRow {
  ts: string;
  [channel: string]: number | null | string;
}

export interface ActiveChannel {
  key: TrendChannel;
  label: string;
  unit: string;
  color: string;
  yAxisId: string;
}

export interface ChartData {
  rows: ChartRow[];
  channels: ActiveChannel[];
  interpreted_query: string;
  days: number;
}

export function sample_rows(rows: ChartRow[], max_points: number): ChartRow[] {
  if (rows.length <= max_points) return rows;
  const step = Math.ceil(rows.length / max_points);
  return rows.filter((_, i) => i % step === 0);
}

export function hour_bucket(iso: string): string {
  const d = new Date(iso);
  d.setMinutes(0, 0, 0);
  return d.toISOString();
}

export function fmt_ts(iso: string): string {
  const d = new Date(iso);
  return `${d.getDate()}.${d.getMonth() + 1} ${String(d.getHours()).padStart(2, "0")}:00`;
}

export function build_y_axis_map(
  channels: TrendChannel[],
): Record<TrendChannel, string> {
  const unit_to_id: Record<string, string> = {};
  let next_id = 0;
  const result: Record<TrendChannel, string> = {} as Record<
    TrendChannel,
    string
  >;
  for (const ch of channels) {
    const unit = TREND_CHANNEL_META[ch].unit;
    if (!(unit in unit_to_id)) {
      unit_to_id[unit] = `y${next_id++}`;
    }
    result[ch] = unit_to_id[unit];
  }
  return result;
}

// The api clients are injectable so the requested-window behaviour can be asserted in a test
// without a live backend; production callers use the real defaults.
export interface ChartDataDeps {
  listMeasurements: typeof listMeasurements;
  getAnalytics: typeof getAnalytics;
}

export async function fetch_chart_data(
  buildingId: string,
  channels: TrendChannel[],
  days: number,
  interpreted_query: string,
  deps: ChartDataDeps = { listMeasurements, getAnalytics },
): Promise<ChartData> {
  const needs_measurements = channels.some((ch) =>
    MEASUREMENT_CHANNELS.has(ch),
  );
  const needs_analytics = channels.some((ch) => ANALYTICS_CHANNELS.has(ch));

  // Bound measurements to the same window analytics already honours (getAnalytics(days)), so a
  // measurement channel and an analytics channel on one chart share a single time range (#193).
  const from_ts = new Date(
    Date.now() - days * 24 * 60 * 60 * 1000,
  ).toISOString();

  const [measurements, analytics] = await Promise.all([
    needs_measurements
      ? deps.listMeasurements(buildingId, 2000, 0, from_ts)
      : Promise.resolve([]),
    needs_analytics
      ? deps.getAnalytics(buildingId, days)
      : Promise.resolve(null),
  ]);

  const row_map = new Map<string, ChartRow>();

  const get_or_create_row = (bucket: string): ChartRow => {
    if (!row_map.has(bucket)) {
      row_map.set(bucket, { ts: bucket });
    }
    return row_map.get(bucket)!;
  };

  for (const m of measurements as MeasurementRecord[]) {
    const bucket = hour_bucket(m.recorded_at);
    for (const ch of channels) {
      if (MEASUREMENT_CHANNELS.has(ch)) {
        const v = m[ch as keyof MeasurementRecord];
        if (typeof v === "number") {
          const row = get_or_create_row(bucket);
          if (row[ch] === undefined) {
            row[ch] = v;
          }
        }
      }
    }
  }

  if (analytics) {
    for (const sample of analytics.hourly_series) {
      const bucket = hour_bucket(sample.hour);
      const row = get_or_create_row(bucket);
      if (channels.includes("occupancy")) row["occupancy"] = sample.occupancy;
      if (channels.includes("ventilation_rate"))
        row["ventilation_rate"] = sample.ventilation_rate;
      if (channels.includes("energy_kwh"))
        row["energy_kwh"] = sample.energy_kwh;
    }
  }

  const sorted = [...row_map.values()].sort((a, b) => a.ts.localeCompare(b.ts));
  const sampled = sample_rows(sorted, 200);

  const y_axis_map = build_y_axis_map(channels);
  const active_channels: ActiveChannel[] = channels.map((ch) => ({
    key: ch,
    yAxisId: y_axis_map[ch],
    ...TREND_CHANNEL_META[ch],
  }));

  return { rows: sampled, channels: active_channels, interpreted_query, days };
}
